'use client';

import React, { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import { LIPS_PARTNERED_LOGO } from '@/components/ui/Logo';
import R2Image from '@/components/ui/R2Image';
import { COMPONENT_STYLES } from '@/constants/styles';
import { getVideoPosterUrl } from '@/lib/assets';
import { ASSET_BASE_URL } from '@/lib/assets/getStaticAssetUrl';
import { useManifest } from '@/lib/manifest-context';
import type { Manifest } from '@/lib/manifest-context';

interface R2VideoProps {
  path: string;
  thumbnailPath?: string;
  videoTitle?: string;
  videoDescription?: string;
  showLogo?: boolean;
  logoPath?: string;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  aspectRatio?: 'video' | 'square' | 'portrait' | 'auto';
  autoPlayOnView?: boolean;
  defaultMuted?: boolean;
  poster?: string;
  allowFullscreen?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
}

const aspectRatioClasses = {
  video: 'aspect-video',
  square: 'aspect-square',
  portrait: 'aspect-3/4',
  auto: '',
};

function resolveAssetUrl(manifest: Manifest, inputPath: string) {
  const fileName = inputPath.split('/').pop() || '';
  const asset = manifest[fileName];

  if (asset) {
    const folderPath = asset.folder ? `${asset.folder}/` : '';
    const url = `${ASSET_BASE_URL}/${folderPath}${asset.hashedName}`;
    const posterAsset = asset.relatedAssets?.find(
      (related) => related.role === 'poster',
    );
    const posterUrl = posterAsset
      ? `${ASSET_BASE_URL}/${folderPath}${posterAsset.hashedName}`
      : undefined;

    return { url, posterUrl };
  }

  const cleanPath = inputPath.startsWith('/') ? inputPath : `/${inputPath}`;
  return { url: `${ASSET_BASE_URL}${cleanPath}`, posterUrl: undefined };
}

const R2Video = ({
  path,
  thumbnailPath,
  videoTitle = 'Our Videos',
  showLogo = true,
  logoPath = LIPS_PARTNERED_LOGO,
  containerClassName,
  containerStyle,
  aspectRatio = 'video',
  autoPlayOnView = true,
  defaultMuted = true,
  poster,
  allowFullscreen = true,
  preload = 'metadata',
}: R2VideoProps) => {
  const manifest = useManifest();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(defaultMuted);
  const [showControls, setShowControls] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPortraitVideo, setIsPortraitVideo] = useState(false);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { url: videoUrl, posterUrl: manifestPosterUrl } = resolveAssetUrl(
    manifest,
    path,
  );

  const thumbnailUrl = thumbnailPath
    ? resolveAssetUrl(manifest, thumbnailPath).url
    : manifestPosterUrl;

  const finalPosterUrl =
    poster ?? manifestPosterUrl ?? getVideoPosterUrl(videoUrl);

  const requestPlay = (node: HTMLVideoElement) => {
    const playPromise = node.play();
    if (typeof playPromise?.catch !== 'function') return;
    playPromise.catch((error: unknown) => {
      const errorName =
        typeof error === 'object' && error !== null && 'name' in error
          ? String((error as { name: unknown }).name)
          : '';
      if (errorName === 'AbortError') return;
      console.error(error);
    });
  };

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!autoPlayOnView) return undefined;

    const node = videoRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!userPaused) requestPlay(node);
        } else {
          node.pause();
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [videoUrl, userPaused, autoPlayOnView]);

  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);
  const handleVideoMetadataLoaded = () => {
    const node = videoRef.current;
    if (!node) return;
    setIsPortraitVideo(node.videoHeight > node.videoWidth);
    setDuration(Number.isFinite(node.duration) ? node.duration : 0);
  };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const node = videoRef.current;
    if (!node) return;
    const time = Number(event.target.value);
    node.currentTime = time;
    setCurrentTime(time);
  };

  const handleTogglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      if (videoRef.current.ended) videoRef.current.currentTime = 0;
      setUserPaused(false);
      requestPlay(videoRef.current);
    } else {
      setUserPaused(true);
      videoRef.current.pause();
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume > 0) {
        setIsMuted(false);
        videoRef.current.muted = false;
      }
    }
  };

  const handleToggleMute = () => {
    if (!videoRef.current) return;
    setIsMuted((prev) => {
      const next = !prev;
      videoRef.current!.muted = next;
      return next;
    });
  };

  const handleToggleFullscreen = async () => {
    const node = containerRef.current;
    if (!node) return;

    try {
      if (!document.fullscreenElement) {
        await node.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen request failed', error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleShowControls = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  };

  const handleHideControls = () => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    setShowControls(false);
  };

  const handleVideoClick = () => {
    if (window.innerWidth < 1024) setShowControls((prev) => !prev);
    handleTogglePlay();
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
      videoRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden ${
        containerClassName?.trim() ||
        `rounded-2xl ${aspectRatioClasses[aspectRatio]}`
      } ${isFullscreen ? 'flex items-center justify-center bg-black' : ''}`}
      style={containerStyle}
      onMouseEnter={handleShowControls}
      onMouseMove={handleShowControls}
      onMouseLeave={handleHideControls}
    >
      {isPortraitVideo && !isFullscreen && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 bg-black"
        />
      )}

      {isNearViewport && (
        <video
          ref={videoRef}
          className={
            isFullscreen
              ? 'max-h-full max-w-full cursor-pointer object-contain'
              : isPortraitVideo
                ? 'absolute inset-0 z-10 h-full w-full cursor-pointer object-contain'
                : 'absolute inset-0 z-10 h-full w-full cursor-pointer object-cover'
          }
          muted={isMuted}
          playsInline
          preload={preload}
          poster={finalPosterUrl}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onEnded={handleVideoPause}
          onLoadedMetadata={handleVideoMetadataLoaded}
          onTimeUpdate={(event) =>
            setCurrentTime(event.currentTarget.currentTime)
          }
          onClick={handleVideoClick}
          style={{
            opacity: isPlaying || currentTime > 0 ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      {thumbnailUrl && !isFullscreen && (
        <Image
          src={thumbnailUrl}
          alt={videoTitle}
          fill
          className={`transform transition-transform duration-500 group-hover:scale-110 ${
            isPortraitVideo ? 'object-contain' : 'scale-105 object-cover'
          }`}
          style={{
            opacity: isPlaying || currentTime > 0 ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out',
          }}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      )}

      {!isFullscreen && (
        <div className="pointer-events-none absolute inset-0 z-20 bg-black/40" />
      )}

      <div
        className={`absolute top-4 left-4 z-40 flex items-center gap-3 transition-opacity duration-300 ${
          showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button
          onClick={handleTogglePlay}
          className="group flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/30 bg-white/20 backdrop-blur-md transition-all duration-200 hover:bg-white/30"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          {isPlaying ? (
            <svg
              className="h-5 w-5 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg
              className="ml-0.5 h-5 w-5 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div
          className="flex h-10 min-w-10 items-center justify-center rounded-full border border-white/30 bg-white/20 px-2 backdrop-blur-md transition-all duration-300"
          onMouseEnter={() => setShowVolumeSlider(true)}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          <button
            onClick={handleToggleMute}
            className="shrink-0 cursor-pointer transition-transform hover:scale-110"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || volume === 0 ? (
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
              </svg>
            )}
          </button>

          <div
            className={`overflow-hidden transition-all duration-300 ${
              showVolumeSlider ? 'ml-2 w-20 opacity-100' : 'ml-0 w-0 opacity-0'
            }`}
          >
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="mb-3 h-1 w-20 cursor-pointer appearance-none rounded-full [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-moz-range-track]:h-3 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:hover:scale-125 [&::-webkit-slider-track]:h-3 [&::-webkit-slider-track]:rounded-full [&::-webkit-slider-track]:bg-transparent"
              aria-label="Volume control"
              style={{
                background: `linear-gradient(to right, white 0%, white ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.4) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.4) 100%)`,
              }}
            />
          </div>
        </div>

        {allowFullscreen && (
          <button
            onClick={handleToggleFullscreen}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/30 bg-white/20 backdrop-blur-md transition-all duration-200 hover:bg-white/30"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9V4.5M15 9h4.5M15 9l5.25-5.25M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 15v4.5M15 15h4.5M15 15l5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.25 3.75H4.5m0 0v3.75m0-3.75L9 9M15.75 3.75h3.75m0 0v3.75m0-3.75L15 9M8.25 20.25H4.5m0 0v-3.75m0 3.75L9 15m6.75 5.25h3.75m0 0v-3.75m0 3.75L15 15"
                />
              </svg>
            )}
          </button>
        )}
      </div>

      <div
        className={`absolute right-4 bottom-4 left-4 z-40 flex items-center gap-3 rounded-lg bg-black/60 px-3 py-2 text-xs text-white transition-opacity duration-300 ${
          showControls || !isPlaying
            ? 'opacity-100'
            : 'opacity-100 lg:opacity-0'
        }`}
      >
        <input
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={Math.min(currentTime, duration || 0)}
          onChange={handleSeek}
          disabled={!duration}
          aria-label="Seek video"
          className="min-w-0 flex-1 cursor-pointer accent-white"
        />
        <span className="shrink-0 tabular-nums">
          {Math.floor(currentTime / 60)}:
          {String(Math.floor(currentTime % 60)).padStart(2, '0')}
          {' / '}
          {Math.floor(duration / 60)}:
          {String(Math.floor(duration % 60)).padStart(2, '0')}
        </span>
      </div>

      {showLogo && (isFullscreen || !isPlaying || isPortraitVideo) && (
        <div className="pointer-events-none absolute top-4 right-4 z-30 sm:top-3 sm:right-3">
          <R2Image
            path={logoPath}
            alt="LIPS Healthcare"
            width={45}
            height={15}
            className="h-[46px] w-[80px] object-contain lg:h-[40px] lg:w-[70px]"
          />
        </div>
      )}

      {!isPlaying && (
        <button
          onClick={handleTogglePlay}
          className="absolute inset-0 z-30 flex cursor-pointer items-center justify-center"
          aria-label="Play video"
        >
          <div
            className={`${COMPONENT_STYLES.CARD.GLASS} flex h-16 w-16 items-center justify-center rounded-full transition-all duration-300 hover:scale-110 lg:h-20 lg:w-20`}
          >
            <div className="ml-1 h-0 w-0 border-t-10 border-r-0 border-b-10 border-l-16 border-transparent border-l-white lg:border-t-12 lg:border-b-12 lg:border-l-20" />
          </div>
        </button>
      )}
    </div>
  );
};

export default R2Video;
